import { store } from '@redux/store';
import { selectConfigurationSegmentApiKey } from 'layout/redux/selectors'

/**
 * The getSegmentKey function returns the Segment API key from the Redux store.
 * key is taken from bhi config and expose through the store.
 * @return A string
 */
const getSegmentApiKey = () => {
  const state = store.getState();
  const segmentApiKey = selectConfigurationSegmentApiKey(state);

  return segmentApiKey;
}

/* `const analyticsInstance` is an object that stores the state of the analytics script. It has three
properties: `isAnalyticsScriptLoaded`, `isAnalyticsScriptLoading`, and `loadingAttemps`. */
const analyticsInstance = {
  isAnalyticsScriptLoaded: false,
  isAnalyticsScriptLoading: false,
  loadingAttemps: 0,
};

/**
 * The onAnalyticsError function is called when the analytics script fails to load.
 * 
 *
 * @param reject Reject the promise
 *
 * @return A promise
 */
const onAnalyticsError = (reject) => {
  console.log('Unable to load analytics script.');
  analyticsInstance.isAnalyticsScriptLoaded = false;
  analyticsInstance.isAnalyticsScriptLoading = false;
  reject(new Error(`Unable to load analytics script.`));
};
  
/**
 * The onAnalyticsLoaded function is called when the analytics script has been loaded.
 * 
 *
 * @param resolve Resolve the promise
 *
 * @return A promise
 */
const onAnalyticsLoaded = (resolve) => {
  analyticsInstance.isAnalyticsScriptLoaded = true;
  resolve();
};


/**
 * The isAnalyticsLoaded function checks if the analytics script has been loaded.
 * If it is, then it resolves the promise with a call to onAnalyticsLoaded. 
 * Otherwise, if loadingAttemps is less than 10, then we increment loadingAttemps and try again in 100ms.
 * If loadingAttempts reaches 10 without resolving the promise, we reject it with a call to onAnalyticsError.
 *
 * @param resolve Call the onanalyticsloaded function
 * @param reject Reject the promise
 *
 * @return A promise
 */
const isAnalyticsLoaded = (resolve, reject) => {  
  if (analyticsInstance.isAnalyticsScriptLoaded) {
    onAnalyticsLoaded(resolve);
  } else if (analyticsInstance.loadingAttemps < 10){
    analyticsInstance.loadingAttemps = + 1;
    setTimeout(isAnalyticsLoaded.bind(null, resolve, reject), 100);
  } else {
    onAnalyticsError(reject);
  }
};


/**
 * The waitForAnalytics function returns a Promise that resolves when the Segment Analytics library has loaded.
 * @return A promise
 */
const waitForAnalytics = () =>
new Promise((resolve, reject) => {
  setTimeout(isAnalyticsLoaded.bind(null, resolve, reject), 100);
});


/**
 * The appendAnalyticsScript function appends the analytics script to the DOM.
 * @param resolve Resolve the promise
 * @param reject Reject the promise
 *
 * @return A promise
 */
const appendAnalyticsScript = (resolve, reject) => {
    
    /** Segment script to laod analytics.js */
    var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="j8bbVDbO2703kwm4gK7ydT9tSV3djzcn";analytics.SNIPPET_VERSION="5.2.0";
    analytics.load(getSegmentApiKey(), {
        integrations: {
          'Segment.io': {
            deliveryStrategy: {
              config: {
                keepalive: true,
              }
            }
          }
        }
      });
    }

    if (window.analytics) {
        onAnalyticsLoaded(resolve);
    } else {
        onAnalyticsError(reject);
    }
};
/** 
* Loads the Segment Analytics script.
* @summary With the use of a internal state it validates if the loading of the script is already happening, if not, it calls to loading or waits if required.
* This to avoid multiple instances to load the library.
* @param {analyticsLoaded} analyticsLoaded - call back to be executed when the load was succesfull
* @param {analyticsErrored} analyticsErrored - call back to be executed when the load was errored
*/
export default function loadAnalytics(analyticsLoaded, analyticsErrored) {
    if (!analyticsInstance.isAnalyticsScriptLoading) {
      // Set the analytics instance as loading to prevent multiple requests.
      analyticsInstance.isAnalyticsScriptLoading = true;
      return new Promise(appendAnalyticsScript).then(analyticsLoaded).catch(analyticsErrored);
    }
  
    if (analyticsInstance.isAnalyticsScriptLoaded) {
      // If the analytics instance is already loaded resolve the promise.
      return Promise.resolve().then(analyticsLoaded).catch(analyticsErrored);
    }
  
    // Otherwise wait to review if the analytics instance has loaded.
    return waitForAnalytics().then(analyticsLoaded).catch(analyticsErrored);
  }